import { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Alert,
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Button,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UsbIcon from '@mui/icons-material/Usb';
import useAuth from '../hooks/useAuth';
import SerialClient from '../services/serialClient';
import { API_ENDPOINT } from '../services/common';
import ConnectPrompt from '../components/sensor-manager/ConnectPrompt';
import DeviceHeader from '../components/sensor-manager/DeviceHeader';
import OverviewTab from '../components/sensor-manager/OverviewTab';
import FilesTab from '../components/sensor-manager/FilesTab';
import FirmwareTab from '../components/sensor-manager/FirmwareTab';
import SettingsTab from '../components/sensor-manager/SettingsTab';
import RulesTab from '../components/sensor-manager/RulesTab';
import FirmwareUpgradeCard from '../components/sensor-manager/FirmwareUpgradeCard';

// ---------------------------------------------------------------------------

const SENSOR_MODEL = 'PTQS1005';

function isSerialSupported() {
  return typeof navigator !== 'undefined' && !!navigator.serial;
}

// ---------------------------------------------------------------------------

export default function SensorManagerPage() {
  const clientRef = useRef(null);
  const logEndRef = useRef(null);

  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN');

  const portRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [passiveFiles, setPassiveFiles] = useState([]);
  const [captureFiles, setCaptureFiles] = useState([]);
  const [logFiles, setLogFiles] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const [serialLog, setSerialLog] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [deviceConfig, setDeviceConfig] = useState(null);
  const [busyOperation, setBusyOperation] = useState(null);
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState(null);
  const [rebootState, setRebootState] = useState(null); // null | { phase, message, attempt }
  const [connectFailed, setConnectFailed] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const serialSupported = isSerialSupported();

  // Create client once on mount
  useEffect(() => {
    clientRef.current = new SerialClient();
    const unsubscribe = clientRef.current.onLog((entry) => {
      setSerialLog((prev) => {
        const next = [...prev, entry];
        return next.length > 500 ? next.slice(-500) : next;
      });
    });
    return () => {
      unsubscribe();
      if (clientRef.current && clientRef.current.isConnected()) {
        clientRef.current.disconnect().catch(() => {});
      }
    };
  }, []);

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [serialLog]);

  // ---- Load device state after connection ----
  const loadDeviceState = useCallback(async (client) => {
    const info = await client.info();
    // protocolVersion (from the HELLO handshake) is the capability signal:
    // < 2 means legacy firmware that should be updated via the ESP Web Tools
    // browser installer rather than the v2 serial OTA.
    setDeviceInfo({ ...info, protocolVersion: client.protocolVersion() });

    const storage = await client.free();
    setStorageInfo(storage);

    const passive = await client.listFiles('/data/passive').catch(() => []);
    setPassiveFiles(passive);
    const captures = await client.listFiles('/data/captures').catch(() => []);
    setCaptureFiles(captures);
    const logs = await client.listFiles('/data/logs').catch(() => []);
    setLogFiles(logs);

    try {
      const config = await client.config();
      setDeviceConfig(config);
    } catch (_) {
      setDeviceConfig(null);
    }
  }, []);

  // ---- Fetch latest firmware version from API ----
  const fetchLatestFirmwareVersion = useCallback(async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/firmware/versions?model=${SENSOR_MODEL}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const stable = data.versions?.filter((v) => !v.prerelease);
        if (stable?.length > 0) {
          setLatestFirmwareVersion(stable[0].version);
        }
      }
    } catch (_) {
      // Best-effort — don't block on firmware version check
    }
  }, []);

  // ---- Connect ----
  const handleConnect = useCallback(async () => {
    try {
      setError(null);
      setConnectFailed(false);
      setShowUpgrade(false);
      const port = await navigator.serial.requestPort();
      portRef.current = port;
      await clientRef.current.connect(port);
      setConnected(true);

      await loadDeviceState(clientRef.current);
      await fetchLatestFirmwareVersion();
    } catch (err) {
      // Don't show connect-failed state if user cancelled the port picker
      if (err.name === 'NotFoundError' || err.message?.includes('No port selected')) {
        return;
      }
      setConnectFailed(true);
      setConnected(false);
    }
  }, [loadDeviceState, fetchLatestFirmwareVersion]);

  // ---- Disconnect ----
  const handleDisconnect = useCallback(async () => {
    try {
      await clientRef.current.disconnect();
    } catch (_) {
      // best effort
    }
    setConnected(false);
    setDeviceInfo(null);
    setStorageInfo(null);
    setPassiveFiles([]);
    setCaptureFiles([]);
    setLogFiles([]);
    setDeviceConfig(null);
    setLatestFirmwareVersion(null);
    setActiveTab(0);
  }, []);

  // ---- Download ----
  const handleDownload = useCallback(async (filePath) => {
    try {
      setDownloading(filePath);
      setBusyOperation('downloading');
      const data = await clientRef.current.readFile(filePath);

      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Download failed');
    } finally {
      setDownloading(null);
      setBusyOperation(null);
    }
  }, []);

  // ---- Delete ----
  const handleDelete = useCallback(async (filePath) => {
    try {
      await clientRef.current.deleteFile(filePath);
      if (filePath.startsWith('/data/passive/')) {
        setPassiveFiles((prev) => prev.filter((f) => `/data/passive/${f.name}` !== filePath));
      } else if (filePath.startsWith('/data/captures/')) {
        setCaptureFiles((prev) => prev.filter((f) => `/data/captures/${f.name}` !== filePath));
      }
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }, []);

  // ---- Refresh file listings ----
  const handleRefreshFiles = useCallback(async (directory) => {
    try {
      setBusyOperation('refreshing');
      const files = await clientRef.current.listFiles(`/${directory}`).catch(() => []);
      if (directory === 'data/passive') setPassiveFiles(files);
      else if (directory === 'data/captures') setCaptureFiles(files);
      else if (directory === 'data/logs') setLogFiles(files);
      const storage = await clientRef.current.free();
      setStorageInfo(storage);
    } catch (err) {
      setError(err.message || 'Failed to refresh files');
    } finally {
      setBusyOperation(null);
    }
  }, []);

  // ---- Reconnect using saved port ----
  const reconnectWithPort = useCallback(async (port) => {
    const newClient = new SerialClient();
    newClient.onLog((entry) => {
      setSerialLog((prev) => {
        const next = [...prev, entry];
        return next.length > 500 ? next.slice(-500) : next;
      });
    });

    await newClient.connect(port);
    clientRef.current = newClient;

    await loadDeviceState(newClient);
    setConnected(true);
  }, [loadDeviceState]);

  // ---- Try to connect to a port (connect() handles open internally) ----
  const tryConnectPort = useCallback(async (port) => {
    portRef.current = port;
    await reconnectWithPort(port);
  }, [reconnectWithPort]);

  // ---- Manual reconnect (from retry button — uses requestPort with user gesture) ----
  const handleReconnect = useCallback(async () => {
    setRebootState((prev) => ({
      ...prev,
      phase: 'reconnecting',
      message: 'Reconnecting...',
    }));
    try {
      await clientRef.current.forceClose().catch(() => {});
      portRef.current = null;

      const port = await navigator.serial.requestPort();
      await tryConnectPort(port);
      setRebootState(null);
      setBusyOperation(null);
    } catch (_) {
      setRebootState({
        phase: 'failed',
        message: 'Could not reconnect to sensor.',
      });
    }
  }, [tryConnectPort]);

  // ---- Auto-reconnect after reboot (no user gesture — uses getPorts) ----
  const autoReconnect = useCallback(async () => {
    const MAX_RETRIES = 8;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      setRebootState({
        phase: 'reconnecting',
        message: `Reconnecting to sensor... (${attempt}/${MAX_RETRIES})`,
        attempt,
        maxRetries: MAX_RETRIES,
      });

      try {
        // getPorts() returns previously-authorized ports without user gesture
        const ports = await navigator.serial.getPorts();
        if (ports.length === 0) {
          throw new Error('No ports available');
        }

        // Try each authorized port
        for (const port of ports) {
          try {
            await tryConnectPort(port);
            setRebootState(null);
            setBusyOperation(null);
            return true;
          } catch (_) {
            try { await port.close(); } catch (__) {}
          }
        }
        throw new Error('No port responded');
      } catch (_) {
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
    }
    return false;
  }, [tryConnectPort]);

  // ---- Flash Firmware ----
  // Shared flash path: stream a binary to the device, then handle the reboot
  // and auto-reconnect. Used by both version-based and file-based flashing.
  const flashBinary = useCallback(async (binary, label, onProgress) => {
    try {
      setError(null);
      setBusyOperation('flashing');

      await clientRef.current.flashFirmware(binary, onProgress);

      // Device is rebooting — show reboot UI
      setConnected(false);
      setDeviceInfo(null);
      setRebootState({
        phase: 'rebooting',
        message: 'Firmware updated successfully! Your sensor is rebooting...',
        version: label,
      });

      // Clean up old client streams and close port properly
      // (forceClose cancels reader/writer before closing — plain port.close()
      // fails silently when streams are still locked)
      await clientRef.current.forceClose();
      portRef.current = null;

      // Wait for device to reboot and USB to re-enumerate
      await new Promise((r) => setTimeout(r, 5000));

      // Auto-reconnect attempt
      const success = await autoReconnect();
      if (!success) {
        setRebootState({
          phase: 'failed',
          message: 'Could not automatically reconnect. The sensor may still be rebooting.',
        });
      }
    } catch (err) {
      setRebootState(null);
      setError(err.message || 'Firmware update failed');
      throw err;
    } finally {
      setBusyOperation(null);
    }
  }, [autoReconnect]);

  const handleFlash = useCallback(async (version, onProgress) => {
    const url = `${API_ENDPOINT}/firmware/download/${SENSOR_MODEL}/v${version}.bin`;
    const response = await fetch(url);
    if (!response.ok) {
      const err = new Error(`Failed to download firmware v${version}: ${response.status}`);
      setError(err.message);
      throw err;
    }
    const binary = new Uint8Array(await response.arrayBuffer());
    await flashBinary(binary, `v${version}`, onProgress);
  }, [flashBinary]);

  // Admin-only: flash an arbitrary local .bin (e.g. an unreleased build).
  const handleFlashFile = useCallback(async (file, onProgress) => {
    const binary = new Uint8Array(await file.arrayBuffer());
    await flashBinary(binary, file.name, onProgress);
  }, [flashBinary]);

  // Older firmware (protocol < 2) can't be updated reliably over the v2 serial
  // OTA, so route it to the ESP Web Tools browser installer (esptool-based,
  // robust). Close the serial connection first — the installer drives the port.
  const isOlderFirmware =
    connected && deviceInfo && deviceInfo.protocolVersion != null && deviceInfo.protocolVersion < 2;

  const handleUpgradeOldFirmware = useCallback(async () => {
    await clientRef.current.forceClose().catch(() => {});
    portRef.current = null;
    setConnected(false);
    setDeviceInfo(null);
    setShowUpgrade(true);
  }, []);

  return (
    <>
      <Helmet>
        <title> VPSI | Sensor Manager </title>
      </Helmet>

      <Container>
        {rebootState ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              textAlign: 'center',
              gap: 2,
            }}
          >
            {(rebootState.phase === 'rebooting' || rebootState.phase === 'reconnecting') && (
              <CircularProgress size={48} />
            )}
            {rebootState.phase === 'failed' && (
              <ErrorOutlineIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            )}

            <Typography variant="h6">
              {rebootState.phase === 'rebooting' && 'Firmware Updated'}
              {rebootState.phase === 'reconnecting' && 'Reconnecting'}
              {rebootState.phase === 'failed' && 'Reconnect Failed'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
              {rebootState.message}
            </Typography>

            {rebootState.phase === 'failed' && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<UsbIcon />}
                  onClick={handleReconnect}
                >
                  Reconnect
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box>
        ) : !connected ? (
          showUpgrade ? (
            <FirmwareUpgradeCard
              onBack={() => {
                setShowUpgrade(false);
                setConnectFailed(false);
              }}
              onConnect={handleConnect}
            />
          ) : connectFailed ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                textAlign: 'center',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Couldn&apos;t connect to your sensor
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 400 }}
              >
                The device didn&apos;t respond. Check your USB cable and try again.
              </Typography>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{ mb: 2 }}
              >
                Retry
              </Button>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowUpgrade(true)}
              >
                Running older firmware? Update your sensor
              </Link>
            </Box>
          ) : (
            <ConnectPrompt onConnect={handleConnect} serialSupported={serialSupported} />
          )
        ) : (
          <>
            <DeviceHeader deviceInfo={deviceInfo} onDisconnect={handleDisconnect} />

            {isOlderFirmware && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={handleUpgradeOldFirmware}>
                    Update now
                  </Button>
                }
              >
                This sensor is running older firmware (v{deviceInfo.firmware}). For a reliable
                update, use the browser installer.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                <Tab label="Overview" />
                <Tab label="Files" />
                <Tab label="Firmware" />
                <Tab label="Settings" />
                <Tab label="Rules" disabled />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <OverviewTab
                serialClient={clientRef.current}
                deviceInfo={deviceInfo}
                storageInfo={storageInfo}
                passiveFiles={passiveFiles}
                captureFiles={captureFiles}
                onTabChange={setActiveTab}
                busyOperation={busyOperation}
                latestFirmwareVersion={latestFirmwareVersion}
              />
            )}
            {activeTab === 1 && (
              <FilesTab
                passiveFiles={passiveFiles}
                captureFiles={captureFiles}
                logFiles={logFiles}
                onDownload={handleDownload}
                onDelete={handleDelete}
                downloading={downloading}
                onRefresh={handleRefreshFiles}
                storageInfo={storageInfo}
                refreshing={busyOperation === 'refreshing'}
              />
            )}
            {activeTab === 2 && (
              <FirmwareTab
                serialClient={clientRef.current}
                deviceInfo={deviceInfo}
                onFlash={handleFlash}
                onFlashFile={handleFlashFile}
                isAdmin={isAdmin}
                connected={connected}
                onError={setError}
                latestFirmwareVersion={latestFirmwareVersion}
              />
            )}
            {activeTab === 3 && (
              <SettingsTab
                serialClient={clientRef.current}
                deviceConfig={deviceConfig}
                setDeviceConfig={setDeviceConfig}
                onError={setError}
                busyOperation={busyOperation}
                setBusyOperation={setBusyOperation}
              />
            )}
            {activeTab === 4 && <RulesTab />}
          </>
        )}

        {/* Serial log — admin only */}
        {isAdmin && serialLog.length > 0 && (
          <Accordion sx={{ mt: 3 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                Serial Log
              </Typography>
              <Chip label={serialLog.length} size="small" />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  p: 1.5,
                }}
              >
                {serialLog.map((entry, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      component="span"
                      sx={{ color: '#6a9955', minWidth: 72, flexShrink: 0 }}
                    >
                      {entry.timestamp.toLocaleTimeString('en-US', {
                        hour12: false,
                        fractionalSecondDigits: 3,
                      })}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: entry.direction === 'tx' ? '#569cd6' : '#ce9178',
                        minWidth: 24,
                        flexShrink: 0,
                        fontWeight: 'bold',
                      }}
                    >
                      {entry.direction === 'tx' ? 'TX' : 'RX'}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        color: entry.type === 'binary' ? '#808080' : '#d4d4d4',
                        fontStyle: entry.type === 'binary' ? 'italic' : 'normal',
                        wordBreak: 'break-all',
                      }}
                    >
                      {entry.data}
                    </Box>
                  </Box>
                ))}
                <div ref={logEndRef} />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Container>
    </>
  );
}
